import { createContext, type PropsWithChildren, useContext, useState } from 'react';

export type ChatScrollPosition = { top: number; atBottom: boolean; anchor?: { id: string; offset: number } };

export type ChatScrollMemory = {
  read: (remoteId: string) => ChatScrollPosition | undefined;
  remember: (remoteId: string, position: ChatScrollPosition) => void;
};

const MAX_REMEMBERED_THREADS = 50;
const ChatScrollMemoryContext = createContext<ChatScrollMemory | null>(null);

function createChatScrollMemory(): ChatScrollMemory {
  const positions = new Map<string, ChatScrollPosition>();
  return {
    read: (remoteId) => positions.get(remoteId),
    remember: (remoteId, position) => {
      positions.delete(remoteId);
      positions.set(remoteId, position);
      if (positions.size > MAX_REMEMBERED_THREADS) {
        const oldest = positions.keys().next().value;
        if (oldest !== undefined) positions.delete(oldest);
      }
    },
  };
}

export function ChatScrollMemoryProvider({
  children,
  identityKey,
  enabled = true,
}: PropsWithChildren<{ identityKey: string; enabled?: boolean }>) {
  const [scope, setScope] = useState(() => ({ identityKey, memory: createChatScrollMemory() }));
  let memory = scope.memory;
  if (scope.identityKey !== identityKey) {
    memory = createChatScrollMemory();
    setScope({ identityKey, memory });
  }
  return (
    <ChatScrollMemoryContext.Provider value={enabled ? memory : null}>{children}</ChatScrollMemoryContext.Provider>
  );
}

export function useChatScrollMemory() {
  return useContext(ChatScrollMemoryContext);
}
